/**
 * Reducing a report column to the single figure a KPI tile shows (SPEC §9.4).
 *
 * The rule lives here rather than in the component so it is testable on its
 * own: which reduction a column gets is a statement about the *numbers*, and
 * getting it wrong shows a plausible figure that means something else.
 */

import { columnTotal, EMPTY, formatCell, isNumericKind } from './format'
import type { Cell, ColumnSpec, VisualSpec } from './types'

export interface KpiTile {
  readonly key: string
  readonly label: string
  readonly value: string
  /** How the figure was arrived at, so a reader can tell sum from latest. */
  readonly basis: string
}

export function kpiTiles(
  visual: VisualSpec,
  columns: readonly ColumnSpec[],
  rows: readonly (readonly Cell[])[],
): KpiTile[] {
  return visual.series.map((series) => {
    const index = columns.findIndex((column) => column.key === series.column)
    const column = columns[index]
    if (column === undefined) {
      throw new Error(`visual ${visual.id} tiles unknown column ${series.column}`)
    }
    const label = series.label ?? column.label
    const values = rows.map((row) => row[index] ?? null)

    if (column.total !== 'none' && isNumericKind(column)) {
      const total = columnTotal(column, values)
      return {
        key: series.column,
        label,
        value: total === null ? EMPTY : formatCell(column, total),
        basis: column.total === 'sum' ? 'Sum of all rows' : 'Average across rows',
      }
    }

    const last = values.length === 0 ? null : (values[values.length - 1] ?? null)
    return {
      key: series.column,
      label,
      value: formatCell(column, last),
      basis: 'Latest row',
    }
  })
}
